/**
 * ADA / WCAG 2.1 AA Accessibility Utilities for Charm Theme
 * Handles heading hierarchy normalization, tooltip cleanup, accessible names,
 * image alt attributes, and hover dismissibility.
 */
(() => {
  function runAccessibilityEnhancements() {
    // 1. WCAG 1.3.1: Heading structure safety check - Ensure at least one visible H1 exists on the page
    const existingH1 = document.querySelector('h1');
    if (!existingH1) {
      const mainContent = document.getElementById('MainContent') || document.querySelector('main') || document.body;
      const h1 = document.createElement('h1');
      h1.className = 'visually-hidden';
      const rawTitle = (document.title || '').split('–')[0].split('|')[0].trim();
      h1.textContent = rawTitle || 'Rustic Strength';
      mainContent.prepend(h1);
    } else {
      const h1Text = (existingH1.innerText || existingH1.textContent || '').trim();
      if (!h1Text) {
        const rawTitle = (document.title || '').split('–')[0].split('|')[0].trim();
        existingH1.setAttribute('aria-label', rawTitle || 'Rustic Strength');
      }
    }

    // 2. WCAG 1.3.1: Rebuy Widget Heading Normalization
    // Demote Rebuy product titles from <h5> to regular presentation so cross-sell carousels don't skip levels
    document.querySelectorAll('.rebuy-product-title, .rebuy-timer-title').forEach((el) => {
      if (el.tagName === 'H5' || el.tagName === 'H4') {
        el.setAttribute('role', 'none');
      }
    });

    // 3. WCAG 1.3.1: Universal Heading Hierarchy Normalizer (prevents H1->H3, H1->H5, H2->H4, H2->H5 skips)
    const headings = Array.from(document.querySelectorAll('h1, h2, h3, h4, h5, h6, [role="heading"]'));
    let currentLevel = 0;

    headings.forEach((heading) => {
      // Skip headings inside closed/inert overlays, modals, and drawers
      if (heading.closest('[inert], [aria-hidden="true"], .modal:not(.modal--visible), .drawer:not(.open)')) {
        return;
      }

      // Determine level
      let level = parseInt(heading.getAttribute('aria-level'), 10);
      if (isNaN(level)) {
        const match = heading.tagName.match(/^H([1-6])$/i);
        level = match ? parseInt(match[1], 10) : 2;
      }

      if (currentLevel === 0) {
        currentLevel = level;
      } else if (level > currentLevel + 1) {
        // Heading level was skipped (e.g., from 1 to 3, or 2 to 4/5)
        const normalizedLevel = currentLevel + 1;
        heading.setAttribute('role', 'heading');
        heading.setAttribute('aria-level', String(normalizedLevel));
        currentLevel = normalizedLevel;
      } else {
        currentLevel = level;
      }
    });

    // 4. WCAG 1.4.13: Strip browser-native tooltips from medals, SVGs, and links
    document.querySelectorAll('.jm-medals__medal-link, .jm-medals__medal, .fade-in-image-wrapper').forEach((el) => {
      el.removeAttribute('title');
      el.querySelectorAll('title, svg[title]').forEach((t) => {
        t.remove();
      });
    });

    document.querySelectorAll('[title]').forEach((el) => {
      const text = (el.innerText || el.textContent || '').trim();
      const ariaLabel = el.getAttribute('aria-label');
      if (text.length > 0 || (ariaLabel && ariaLabel.trim().length > 0)) {
        el.removeAttribute('title');
      } else {
        const titleVal = el.getAttribute('title');
        if (titleVal && titleVal.trim()) {
          el.setAttribute('aria-label', titleVal.trim());
          el.removeAttribute('title');
        }
      }
    });

    // 5. WCAG 1.1.1: Ensure all images have an alt attribute
    const images = document.querySelectorAll('img:not([alt])');
    images.forEach((img) => {
      img.setAttribute('alt', img.getAttribute('title') || '');
    });

    // 6. WCAG 4.1.2: Ensure all interactive links and buttons have an accessible name
    const interactiveElements = document.querySelectorAll('a, button, [role="button"], [role="link"]');
    interactiveElements.forEach((el) => {
      const text = (el.innerText || el.textContent || '').trim();
      const ariaLabel = el.getAttribute('aria-label');
      const hasImgAlt = !!el.querySelector('img[alt]:not([alt=""])');

      if ((!ariaLabel || !ariaLabel.trim()) && !text && !hasImgAlt) {
        if (el.closest('.jdgm-widget, #judgeme_product_reviews, .jdgm-rev-widg, .jm-medals')) {
          el.setAttribute('aria-label', 'View customer reviews');
        } else if (el.classList.contains('jm-medals__medal-link')) {
          el.setAttribute('aria-label', 'View Judge.me Verified Reviews');
        } else if (el.classList.contains('drawer__close')) {
          el.setAttribute('aria-label', 'Close drawer');
        } else if (el.classList.contains('modal__close')) {
          el.setAttribute('aria-label', 'Close modal');
        } else if (el.classList.contains('product__color-swatch-option')) {
          const colorVal = el.getAttribute('data-value') || el.getAttribute('title') || 'color';
          el.setAttribute('aria-label', `Select ${colorVal}`);
        } else {
          const title = el.getAttribute('title');
          if (title && title.trim()) {
            el.setAttribute('aria-label', title.trim());
          } else {
            el.setAttribute('aria-label', 'Learn more');
          }
        }
      }
    });

    // 7. WCAG 1.4.13: Secondary hover images marked decorative & dismissible with Escape key
    document.querySelectorAll('.secondary-image').forEach((img) => {
      img.setAttribute('aria-hidden', 'true');
    });

    // 8. WCAG 2.4.11 / 1.3.1: Mark closed app review modals inert and aria-hidden
    const reviewModals = document.querySelectorAll('.jdgm-write-review-modal, .jdgm-modal');
    reviewModals.forEach((modal) => {
      if (!modal.classList.contains('jdgm-modal--open') && !modal.classList.contains('jdgm--visible')) {
        modal.setAttribute('aria-hidden', 'true');
        modal.setAttribute('inert', '');
        modal.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((h) => {
          h.setAttribute('aria-hidden', 'true');
        });
      }
    });
  }

  // Run on initial DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runAccessibilityEnhancements);
  } else {
    runAccessibilityEnhancements();
  }

  // Escape key handler for hover dismissibility (WCAG 1.4.13)
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' || e.key === 'Esc') {
      document.body.classList.add('hide-hover-content');
      setTimeout(() => {
        document.body.classList.remove('hide-hover-content');
      }, 800);
    }
  });

  // Re-run debounced on dynamic DOM mutations (for Rebuy widgets, Junip/Judge.me reviews, etc.)
  let mutationTimeout = null;
  const observer = new MutationObserver((mutations) => {
    const hasExternalChanges = mutations.some((m) => {
      return (
        m.type === 'childList' &&
        Array.from(m.addedNodes).some((n) => n.nodeType === 1 && !n.classList?.contains('visually-hidden'))
      );
    });

    if (hasExternalChanges) {
      clearTimeout(mutationTimeout);
      mutationTimeout = setTimeout(runAccessibilityEnhancements, 250);
    }
  });

  observer.observe(document.body || document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
